import {
  ClipboardCheckIcon,
  DownloadIcon,
  FileCheckIcon,
  FileClockIcon,
  PaperclipIcon,
  PenToolIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EntitySheet } from "@/components/entity-sheet";
import { cn } from "@/lib/utils";
import { useInspectionItemsQuery } from "../../../services/api";
import {
  clearInspectionReport,
  generateInspectionPdf,
  startInspectionPdfGeneration,
} from "../../../pages/inspections/see-inspection/actions";

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface PersonRef {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface InspectionRowForSheet {
  id: string | number;
  resource?: {
    id?: string | number;
    name?: string;
    tags?: TagRef[];
  };
  flow?: { id?: string | number; name?: string };
  controlDate?: string;
  conformity?: { id?: number };
  assignedToUser?: PersonRef | null;
  submittedByUser?: PersonRef | null;
  /** Pre-generated report URL; when present the Download button just
   *  opens it in a new tab. Falsy → dispatch the legacy PDF thunks. */
  reportUrl?: string | null;
  /** Parsed signatures payload — Control.toJSON only includes the key
   *  when at least one signature was captured, so any truthy value
   *  means the inspection was signed. */
  signatures?: Record<string, unknown> | unknown[] | null;
  /** Parsed evaluation-form answers; same truthy-only contract. */
  evaluationForm?: Record<string, unknown> | null;
  /** Report state enum (backend): 0 = GENERATING, 1 = AVAILABLE. */
  reportState?: number | null;
}

interface InspectionSheetProps {
  value: InspectionRowForSheet | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Navigate to the full-page detail view (`/dashboard/inspections/:id`). */
  onExpand: () => void;
  /** Optional destructive trash icon in the header. */
  onDelete?: () => void;
}

/*
 * Slim summary panel for an inspection. The full editing/inspection
 * experience (item-by-item flow, media gallery, signatures, PDF
 * generation) lives at /dashboard/inspections/:id; this sheet is the
 * Jira-style "row preview" surface that lets the user verify what they
 * clicked before either acting from the list or opening the full view.
 */
interface InspectionItemRow {
  conformity?: { id?: number };
  Item?: { title?: string };
  description?: string;
  /** Rich attachments list (v2 client). */
  attachments?: Array<unknown> | null;
  /** Legacy URL list (v1 client). */
  photos?: string[] | null;
}

function attachmentCount(row: InspectionItemRow): number {
  if (Array.isArray(row.attachments)) return row.attachments.length;
  if (Array.isArray(row.photos)) return row.photos.length;
  return 0;
}

const ITEMS_PREVIEW_LIMIT = 8;

function itemConformityClass(id: number | undefined) {
  if (id === 1) return "text-emerald-600 dark:text-emerald-400";
  if (id === 2) return "text-sky-600 dark:text-sky-400";
  if (id === 3) return "text-muted-foreground";
  return "text-destructive";
}

function itemConformityLabel(
  id: number | undefined,
  t: (k: string, d?: string) => string,
): string {
  if (id === 1) return t("inspections.items.yes");
  if (id === 2) return t("inspections.items.scheduled");
  if (id === 3) return t("inspections.items.na", "N/A");
  return t("inspections.items.no");
}

export function InspectionSheet({
  value,
  open,
  onOpenChange,
  onExpand,
  onDelete,
}: InspectionSheetProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Pull the inspected items lazily. The endpoint accepts a `range` so we
  // ask for the first page only — the sheet is a summary surface, anything
  // past `ITEMS_PREVIEW_LIMIT` falls through to "+N more → full view".
  //
  // `currentData` (not `data`) is the right field here: when the controlId
  // changes, RTK Query keeps the previous cache entry's `data` populated
  // until the new fetch resolves, which leaks the prior inspection's items
  // into the new row's preview. `currentData` clears immediately on args
  // change, so combined with `isFetching` it gives us a clean skeleton.
  const controlId = value?.id != null ? String(value.id) : "";
  const { currentData: itemsResponse, isFetching: itemsLoading } =
    useInspectionItemsQuery(
      { controlId, range: { page: 1 } },
      { skip: !open || !controlId },
    ) as {
      currentData?: InspectionItemRow[] | { error?: unknown };
      isFetching: boolean;
    };

  const items: InspectionItemRow[] = Array.isArray(itemsResponse)
    ? itemsResponse
    : [];
  const previewItems = items.slice(0, ITEMS_PREVIEW_LIMIT);
  const remainingCount = Math.max(0, items.length - previewItems.length);
  const showItemsSkeleton = itemsLoading && previewItems.length === 0;

  const conformityId = value?.conformity?.id;
  const conformityClass =
    conformityId === 1
      ? "text-emerald-600 dark:text-emerald-400"
      : conformityId === 2
      ? "text-sky-600 dark:text-sky-400"
      : "text-destructive";
  const conformityLabel =
    conformityId === 1
      ? t("inspections.items.yes")
      : conformityId === 2
      ? t("inspections.items.scheduled")
      : t("inspections.items.no");

  const assignedName =
    value?.assignedToUser &&
    `${value.assignedToUser.firstName ?? ""} ${value.assignedToUser.lastName ?? ""}`.trim();
  const submittedName =
    value?.submittedByUser &&
    `${value.submittedByUser.firstName ?? ""} ${value.submittedByUser.lastName ?? ""}`.trim();

  // Conformity id 2 = SCHEDULED — there's nothing to download yet, so the
  // see-inspection page hides the action in that state; mirror that here.
  const canDownload = value != null && value.conformity?.id !== 2;

  // Preview-only signals: the sheet stays a row preview, so for things
  // like signatures or evaluation answers we don't render the payload —
  // just a small chip so the user knows the artefact exists before
  // expanding to the full view.
  const signaturesCount = Array.isArray(value?.signatures)
    ? value.signatures.length
    : value?.signatures && typeof value.signatures === "object"
    ? Object.values(value.signatures).filter(Boolean).length
    : 0;
  const hasSignatures = signaturesCount > 0;
  const hasEvaluation =
    value?.evaluationForm != null &&
    typeof value.evaluationForm === "object" &&
    Object.keys(value.evaluationForm).length > 0;
  const reportAvailable = value?.reportState === 1;
  const reportGenerating = value?.reportState === 0;
  const hasAnyPill =
    hasSignatures || hasEvaluation || reportAvailable || reportGenerating;

  function onDownloadReport() {
    if (!value) return;
    if (value.reportUrl) {
      window.open(value.reportUrl, "_blank");
      return;
    }
    // No pre-generated PDF. We have to open the popup SYNCHRONOUSLY in
    // the click handler or the browser blocks it as programmatic — but
    // we can't close that popup on empty/error responses either, since
    // "tab opens and immediately vanishes" reads as a broken download.
    //
    // Strategy: write a small loading shell into the popup immediately,
    // then replace its document with the report HTML when the thunk
    // resolves. On error/empty we replace the shell with a friendly
    // message so the user knows what happened instead of staring at a
    // blank pop-up.
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(
        `<!doctype html><title>${t("inspections.see-inspection.download")}</title>` +
          `<body style="font-family:system-ui;padding:24px;color:#374151">` +
          `${t("common.loading", "Loading…")}` +
          `</body>`,
      );
    }
    const inspectionId = value.id;
    Promise.resolve(dispatch(generateInspectionPdf(inspectionId)))
      .then((html: unknown) => {
        if (!win || win.closed) return;
        if (typeof html === "string" && html.trim().length > 0) {
          win.document.open();
          win.document.write(html);
          win.document.close();
          return;
        }
        // Empty body — generation hasn't finished yet. The POST below
        // kicks off the async job, so guide the user to retry shortly.
        win.document.open();
        win.document.write(
          `<!doctype html><title>${t("inspections.see-inspection.download")}</title>` +
            `<body style="font-family:system-ui;padding:24px;color:#374151">` +
            `${t(
              "inspections.see-inspection.report-not-ready",
              "The report is being generated. Please try again in a moment.",
            )}` +
            `</body>`,
        );
        win.document.close();
      })
      .catch(() => {
        if (!win || win.closed) return;
        win.document.open();
        win.document.write(
          `<!doctype html><title>${t("inspections.see-inspection.download")}</title>` +
            `<body style="font-family:system-ui;padding:24px;color:#b91c1c">` +
            `${t("common.api-error")}` +
            `</body>`,
        );
        win.document.close();
      })
      .finally(() => {
        // The thunk also stored the payload in the see-inspection slice
        // via GENERATE_INSPECTION_PDF_SUCCESS — clear it so a subsequent
        // visit to the full view doesn't auto-pop the same report.
        dispatch(clearInspectionReport());
      });
    // Kick off the async server-side PDF job in parallel — unrelated to
    // the popup above, kept in sync with how the full-page download has
    // always worked.
    dispatch(startInspectionPdfGeneration(inspectionId));
  }

  return (
    <EntitySheet
      open={open}
      onOpenChange={onOpenChange}
      title={value?.resource?.name ?? t("sidebar.inspections")}
      description={value?.flow?.name}
      canEdit={false}
      onExpand={onExpand}
      onDelete={onDelete}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canDownload ? (
            <Button variant="outline" onClick={onDownloadReport}>
              <DownloadIcon className="size-4" />
              {t("inspections.see-inspection.download")}
            </Button>
          ) : null}
          <Button onClick={onExpand}>
            {t("common.open-full-view")}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <dl className="space-y-3 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("inspections.items.appropriate")}
            </dt>
            <dd className={cn(conformityClass)}>{conformityLabel}</dd>
          </div>
          {value?.controlDate ? (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("inspections.items.date")}
              </dt>
              <dd>{value.controlDate}</dd>
            </div>
          ) : null}
          {assignedName ? (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("inspections.filter.controller", "Controller")}
              </dt>
              <dd>{assignedName}</dd>
            </div>
          ) : null}
          {submittedName ? (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("inspections.info-card.employee")}
              </dt>
              <dd>{submittedName}</dd>
            </div>
          ) : null}
        </dl>

        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("resource.items.tags")}
          </h3>
          {value?.resource?.tags && value.resource.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {value.resource.tags.map((tag) => (
                <Badge
                  key={tag.id}
                  style={{
                    backgroundColor: tag.color ?? undefined,
                    color: tag.color ? "#fff" : undefined,
                  }}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("tags.empty")}</p>
          )}
        </section>

        {hasAnyPill ? (
          <div className="flex flex-wrap gap-1.5">
            {hasSignatures ? (
              <Badge variant="secondary" className="gap-1">
                <PenToolIcon className="size-3" />
                {signaturesCount > 1
                  ? t("inspections.sheet.signatures-count", {
                      count: signaturesCount,
                      defaultValue: "{{count}} signatures",
                    })
                  : t("inspections.sheet.signed", "Signed")}
              </Badge>
            ) : null}
            {hasEvaluation ? (
              <Badge variant="secondary" className="gap-1">
                <ClipboardCheckIcon className="size-3" />
                {t("inspections.sheet.evaluation-filled", "Evaluation")}
              </Badge>
            ) : null}
            {reportAvailable ? (
              <Badge variant="secondary" className="gap-1">
                <FileCheckIcon className="size-3" />
                {t("inspections.sheet.report-ready", "Report ready")}
              </Badge>
            ) : reportGenerating ? (
              <Badge variant="secondary" className="gap-1">
                <FileClockIcon className="size-3" />
                {t("inspections.sheet.report-generating", "Generating report…")}
              </Badge>
            ) : null}
          </div>
        ) : null}

        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t(
              "inspections.see-inspection.inspection-items",
              "Inspected items",
            )}
          </h3>
          {showItemsSkeleton ? (
            <ul className="divide-y divide-border rounded-md border border-border">
              {Array.from({ length: 4 }).map((_, idx) => (
                <li
                  key={idx}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  aria-hidden
                >
                  <Skeleton className="h-4 w-3/5" />
                  <Skeleton className="h-4 w-10" />
                </li>
              ))}
            </ul>
          ) : previewItems.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border border-border">
              {previewItems.map((row, idx) => {
                const id = row.conformity?.id;
                const attachments = attachmentCount(row);
                return (
                  <li
                    key={idx}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {row.Item?.title ?? row.description ?? "—"}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {attachments > 0 ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"
                          aria-label={t("inspections.items.attachments", {
                            number: attachments,
                          })}
                        >
                          <PaperclipIcon className="size-3" />
                          {attachments}
                        </span>
                      ) : null}
                      <span
                        className={cn(
                          "text-xs font-medium",
                          itemConformityClass(id),
                        )}
                      >
                        {itemConformityLabel(id, t)}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t(
                "inspections.see-inspection.no-items",
                "No items inspected yet",
              )}
            </p>
          )}
          {remainingCount > 0 ? (
            <button
              type="button"
              onClick={onExpand}
              className="mt-2 text-xs text-primary hover:underline"
            >
              {t("inspections.see-inspection.more-items", {
                count: remainingCount,
                defaultValue: "+{{count}} more — open full view",
              })}
            </button>
          ) : null}
        </section>
      </div>
    </EntitySheet>
  );
}
